import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {Line} from 'shells/divider/line';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {MessageContainer} from 'shells/message/message-container';
import {RightFrame, ModalMain, NavButtons} from 'shells/modal/step-modal/layout';
import {OptionCard} from 'shells/option-card/option-card';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {ModalHeader} from 'shells/typography/modal-header';
import {Subheader} from 'shells/typography/subheader';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser_phoneIntegrations as PhoneIntegration} from 'nutshell-graphql-types';

import {
    getClickToCallUpgradePillProps,
    getPreselectedPhoneIntegration,
    getServiceNoun,
    getServiceTypeIconForConnectionWidgets,
} from '../../calls/helpers';
import {useGetSessionUser} from '../../core/use-get-session-user';

import {InitialButton} from '../integrations/initial-button';

import {AircallIntegrationWidget} from './aircall-integration-widget';
import {useSetDialpadIntegrationIsEnabled} from './graphql/hooks/use-set-dialpad-integration-is-enabled';
import {useSetRingCentralIntegrationIsEnabled} from './graphql/hooks/use-set-ringcentral-integration-is-enabled';

import styles from './choose-provider.module.css';

type Props = {
    prevPage: string;
    history: RouterHistory;
    search: string;
};

export function ChooseProvider(props: Props) {
    const {prevPage, history} = props;

    const [isAddingIntegration, setIsAddingIntegration] = React.useState<boolean>(false);
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

    const {user, isLoading, refetch} = useGetSessionUser();
    const phoneIntegrations: PhoneIntegration[] = user ? user.phoneIntegrations : [];
    const clickToCallIntegration = user ? user.clickToCallIntegration : null;

    const canUseClickToCall = NutCapabilities.clickToCall.isAvailable;
    const clickToCallTitle = `Nutshell ${getServiceNoun('CLICKTOCALL')}`;
    const clickToCallIcon = getServiceTypeIconForConnectionWidgets('CLICKTOCALL');

    const {setDialpadIntegrationIsEnabled} = useSetDialpadIntegrationIsEnabled();
    const {setRingCentralIntegrationIsEnabled} = useSetRingCentralIntegrationIsEnabled();

    const preselectedPhoneIntegration = getPreselectedPhoneIntegration(
        phoneIntegrations,
        props.search
    );

    const integrationTypeToHookMap: Partial<
        Record<ExternalServiceType, (arg1: boolean) => Promise<any>>
    > = {
        DIALPAD: setDialpadIntegrationIsEnabled,
        RINGCENTRAL: setRingCentralIntegrationIsEnabled,
    };

    function getIntegrationWidget(phoneIntegration: PhoneIntegration): React.ReactElement {
        const connectOnMount = Boolean(
            preselectedPhoneIntegration &&
                preselectedPhoneIntegration.integrationType === phoneIntegration.integrationType &&
                errorMessages.length === 0
        );

        switch (phoneIntegration.integrationType) {
            case 'AIRCALL':
                return (
                    <AircallIntegrationWidget
                        phoneIntegration={phoneIntegration}
                        sendErrorMessagesToParent={setErrorMessages}
                        setIsAddingIntegration={setIsAddingIntegration}
                        history={history}
                        connectOnMount={connectOnMount}
                    />
                );
            case 'CALLRAIL':
                return (
                    <IntegrationWidget
                        status='initial'
                        account={null}
                        initialText={`Connect with ${getServiceNoun(
                            phoneIntegration.integrationType
                        )}`}
                        logo={getServiceTypeIconForConnectionWidgets(
                            phoneIntegration.integrationType
                        )}
                        disabled={false}
                        connectOnMount={connectOnMount}
                        onConnectClick={() => {
                            history.push(
                                '/connect-phone-integration/callrail-setup?integrationType=CALLRAIL'
                            );
                        }}
                        onDisconnectClick={() => {}}
                        fullWidthInitialButton={true}
                    />
                );
            case 'CLICKTOCALL':
                return (
                    <OptionCard
                        onClick={() => {
                            history.replace(`/connect-phone-integration/click-to-call/setup`);
                        }}
                        title='Click-to-call'
                        body='Make and log unlimited calls from your browser with AI-powered summaries and transcriptions.'
                        icon={getServiceTypeIconForConnectionWidgets('CLICKTOCALL')}
                    />
                );
            case 'KIXIE':
                return (
                    <IntegrationWidget
                        status='initial'
                        account={null}
                        initialText={`Connect with ${getServiceNoun(
                            phoneIntegration.integrationType
                        )}`}
                        logo={getServiceTypeIconForConnectionWidgets(
                            phoneIntegration.integrationType
                        )}
                        disabled={false}
                        connectOnMount={connectOnMount}
                        onConnectClick={() => {
                            history.push(
                                '/connect-phone-integration/kixie-setup?integrationType=KIXIE'
                            );
                        }}
                        onDisconnectClick={() => {}}
                        fullWidthInitialButton={true}
                    />
                );
            default:
                return (
                    <InitialButton
                        initialText={`Connect with ${getServiceNoun(
                            phoneIntegration.integrationType
                        )}`}
                        authCheckUrl={getAuthCheckUrl(phoneIntegration.integrationType)}
                        windowFeatures={{
                            toolbar: 'no',
                            location: 'no',
                            width: 600,
                            height: 850,
                        }}
                        logo={getServiceTypeIconForConnectionWidgets(
                            phoneIntegration.integrationType
                        )}
                        isFullWidth={true}
                        connectOnMount={connectOnMount}
                        enable={() =>
                            // @ts-expect-error
                            integrationTypeToHookMap[phoneIntegration.integrationType](true)
                                .then((response) => {
                                    return {
                                        data: response.data,
                                    };
                                })
                                .catch(() => {
                                    setErrorMessages(['Something went wrong. Please try again.']);
                                })
                        }
                        onAuthStart={() => {
                            setErrorMessages([]);
                            setIsAddingIntegration(true);
                        }}
                        onAuthSuccess={() => {
                            return refetch().then(() => {
                                history.replace(
                                    `/connect-phone-integration/sync-preferences?integrationType=${phoneIntegration.integrationType}`
                                );
                                setIsAddingIntegration(false);
                            });
                        }}
                        onAuthError={(errMsgs) => {
                            setErrorMessages(errMsgs);
                            setIsAddingIntegration(false);
                        }}
                    />
                );
        }
    }

    const banners = (
        <div className='mr-16'>
            <MessageContainer>
                {errorMessages.map((message) => (
                    <FlashBanner key={message} hasBorder={true} type='error'>
                        {message}
                    </FlashBanner>
                ))}
            </MessageContainer>
        </div>
    );

    return (
        <RightFrame>
            {isLoading || !user || isAddingIntegration ? (
                <div className='flex align-center justify-center flex-grow'>
                    <LoadingPage size={64} fill={colors.blueLt} />
                </div>
            ) : (
                <ModalMain fullheight={true}>
                    {banners}
                    <ModalHeader>Connect to your current phone system</ModalHeader>
                    <Subheader>Sync and summarize your calls with Nutshell</Subheader>
                    <div className={styles['grid-container']}>
                        {preselectedPhoneIntegration ? (
                            <>
                                {!preselectedPhoneIntegration.isEnabledForUser &&
                                    getIntegrationWidget(preselectedPhoneIntegration)}
                            </>
                        ) : (
                            phoneIntegrations.map(
                                (phoneIntegration) =>
                                    !phoneIntegration.isEnabledForUser && (
                                        <div key={phoneIntegration.integrationType}>
                                            {getIntegrationWidget(phoneIntegration)}
                                        </div>
                                    )
                            )
                        )}
                    </div>
                    {clickToCallIntegration && !clickToCallIntegration.isEnabled && (
                        <>
                            <div className='mt-12 mb-12 '>
                                <Line text='or' />
                            </div>
                            <Stack spacing={16}>
                                <Subheader>
                                    Don’t have a phone system? Use Nutshell’s built-in calling
                                </Subheader>
                                <SpacerGroup>
                                    <OptionCard
                                        upgradePillProps={
                                            canUseClickToCall
                                                ? undefined
                                                : getClickToCallUpgradePillProps()
                                        }
                                        title={clickToCallTitle}
                                        body='Make and log unlimited calls from your browser with AI-powered summaries and transcriptions.'
                                        icon={clickToCallIcon}
                                        onClick={
                                            canUseClickToCall
                                                ? () => {
                                                      history.replace(
                                                          `/connect-phone-integration/click-to-call/setup`
                                                      );
                                                  }
                                                : undefined
                                        }
                                    />
                                </SpacerGroup>
                            </Stack>
                        </>
                    )}
                </ModalMain>
            )}
            <NavButtons>
                {/* @ts-expect-error */}
                <Link as={RoutedLink} to={prevPage}>
                    Back
                </Link>
                <Button variant='secondary' size='big' disabled={true}>
                    Continue
                </Button>
            </NavButtons>
        </RightFrame>
    );
}

function getAuthCheckUrl(integrationType: ExternalServiceType): string {
    if (integrationType === 'RINGCENTRAL') {
        return 'ring-central/has-auth';
    }

    return `${integrationType.toLowerCase()}/has-auth`;
}
